import { useQuery } from '@tanstack/react-query'
import Fuse from 'fuse.js'
import { useTranslation } from 'next-i18next'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { fetchFeedbackData, setFeedbackData } from '../../../functions/dataHelpers'
import { useFeedbackStore, useStore } from '../../../functions/store'
import Sidebar from './Sidebar'
import FightersCards from './fighterCards'

interface SearchableFighter {
  id: string
  name: string
  phoneNumber: string
  index: number
}

function DashboardFeedback() {
  const { t } = useTranslation('avaliacao360')
  const userStore = useStore((state) => state)
  const feedbackStore = useFeedbackStore((state) => state)
  const { indexSelect } = feedbackStore.feedbackActions
  const ratings = feedbackStore.ratings
  const error = feedbackStore.error

  // Load feedback data
  const { isLoading } = useQuery({
    queryKey: ['feedback', userStore.phoneNumber, userStore.group?._id],
    queryFn: async () => {
      const data = await fetchFeedbackData(
        userStore.phoneNumber,
        userStore?.group?._id,
      )

      if (!data) return

      await setFeedbackData({
        data,
        feedbackHook: feedbackStore,
        userData: userStore,
      })

      return true
    },
    enabled: !!userStore.phoneNumber && ratings.length === 0,
  })

  const [searchQuery, setSearchQuery] = useState('')
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Convert ratings to searchable items
  const searchableItems: SearchableFighter[] = useMemo(() => {
    return ratings.map((rating, index) => ({
      id: rating.fighterPhone,
      name: rating.fighterName || '',
      phoneNumber: rating.fighterPhone,
      index,
    }))
  }, [ratings])

  // Configure Fuse.js
  const fuse = useMemo(() => {
    return new Fuse(searchableItems, {
      keys: ['name', 'phoneNumber'],
      threshold: 0.4,
      distance: 100,
      includeMatches: true,
      minMatchCharLength: 2,
      ignoreLocation: true,
    })
  }, [searchableItems])

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return []
    return fuse.search(searchQuery).slice(0, 8)
  }, [fuse, searchQuery])

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsSearchOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isSearchOpen || searchResults.length === 0) {
      if (e.key === 'ArrowDown' && searchQuery.trim()) {
        setIsSearchOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) =>
          prev < searchResults.length - 1 ? prev + 1 : prev,
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          handleSelect(searchResults[selectedIndex].item)
        }
        break
      case 'Escape':
        setIsSearchOpen(false)
        setSelectedIndex(-1)
        break
    }
  }

  const handleSelect = (item: SearchableFighter) => {
    indexSelect(item.index)
    setSearchQuery('')
    setIsSearchOpen(false)
    setSelectedIndex(-1)
  }

  const openHelpVideos = () => {
    window.open(
      'https://youtube.com/playlist?list=PLW_eqSVhj4cwp8PDyDHHNhx8LV7tikIXM&si=qNogoWcDPAU6QGVm',
      '_blank',
    )
  }

  // Highlight match function
  const highlightMatch = (
    text: string,
    indices: Array<[number, number]> | undefined,
  ): React.ReactNode => {
    if (!indices || indices.length === 0) return text

    const result: React.ReactNode[] = []
    let lastIndex = 0

    indices.forEach(([start, end], i) => {
      if (start > lastIndex) {
        result.push(<span key={`t-${i}`}>{text.slice(lastIndex, start)}</span>)
      }
      result.push(
        <mark
          key={`m-${i}`}
          className="bg-red-400/30 text-white rounded px-0.5"
        >
          {text.slice(start, end + 1)}
        </mark>,
      )
      lastIndex = end + 1
    })

    if (lastIndex < text.length) {
      result.push(<span key="end">{text.slice(lastIndex)}</span>)
    }

    return <>{result}</>
  }

  const getMatchIndices = (
    matches:
      | ReadonlyArray<{
          indices: ReadonlyArray<[number, number]>
          key?: string
        }>
      | undefined,
    key: string,
  ): Array<[number, number]> => {
    const indices = matches?.find((m) => m.key === key)?.indices
    if (!indices) return []
    // Normalize readonly tuple arrays into a mutable array for our renderer.
    return indices.map(([start, end]) => [start, end] as [number, number])
  }

  return (
    <div className="text-white">
      {error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
          <p className="text-red-200">{error}</p>
          <p className="text-slate-300 mt-2 text-sm">
            {t(
              'evaluation.accountRequiredHint',
              'Se você ainda não tem conta no app Goodfellas, crie sua conta para participar da Avaliação 360.',
            )}
          </p>
        </div>
      ) : null}
      {/* Search Bar and Help Button - Fixed at top */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
        {/* Search Bar */}
        <div className="flex-1 relative" ref={searchRef}>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setIsSearchOpen(e.target.value.trim().length > 0)
                setSelectedIndex(-1)
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => searchQuery.trim() && setIsSearchOpen(true)}
              placeholder={t(
                'search.placeholder',
                'Buscar por nome ou telefone...',
              )?.toString()}
              className="w-full pl-12 pr-10 py-3 bg-slate-800/60 border border-slate-600/30 text-white placeholder-slate-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50 text-sm transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('')
                  setIsSearchOpen(false)
                  inputRef.current?.focus()
                }}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-white transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Search Dropdown */}
          {isSearchOpen && (
            <div className="absolute z-50 mt-2 w-full bg-slate-800 border border-slate-600/50 rounded-xl shadow-xl max-h-72 overflow-y-auto">
              {searchResults.length > 0 ? (
                <ul className="py-2">
                  {searchResults.map((result, index) => (
                    <li
                      key={result.item.id}
                      onClick={() => handleSelect(result.item)}
                      className={`px-4 py-3 cursor-pointer transition-colors ${
                        index === selectedIndex
                          ? 'bg-red-500/20 text-white'
                          : 'text-slate-200 hover:bg-slate-700/50'
                      }`}
                    >
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium">
                          {highlightMatch(
                            result.item.name || `{${result.item.phoneNumber}}`,
                            getMatchIndices(result.matches, 'name'),
                          )}
                        </span>
                        <span className="text-xs text-slate-400">
                          {highlightMatch(
                            result.item.phoneNumber,
                            getMatchIndices(result.matches, 'phoneNumber'),
                          )}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : searchQuery.trim() ? (
                <div className="px-4 py-6 text-center text-slate-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 mx-auto mb-2 text-slate-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-sm">
                    {t('search.noResults', 'Nenhum resultado encontrado')}
                  </p>
                  <p className="text-xs mt-1 text-slate-500">
                    {t('search.tryAgain', 'Tente outro termo de busca')}
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Help Button */}
        <button
          onClick={openHelpVideos}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all text-sm font-medium whitespace-nowrap shadow-lg shadow-red-900/30"
          title={t('help.watchVideos', 'Ver tutoriais')?.toString()}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="hidden sm:inline">
            {t('help.watchVideos', 'Ver tutoriais')}
          </span>
        </button>
      </div>

      {/* Main Content - Cards directly visible */}
      <div className="flex flex-col md:flex-row justify-center gap-4">
        <div className="md:block">
          {!isLoading && <Sidebar />}
        </div>
        <FightersCards />
      </div>

      {/* Mobile Sidebar is handled inside Sidebar component */}
    </div>
  )
}

export default DashboardFeedback
